import React, { useEffect, useState } from 'react';
import {
    Box,
    Divider,
    MenuItem,
    FormGroup,
    FormControlLabel,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    ILocaleChoices,
    useEmployeeCreateAndInviteMutation,
    useCompanyBudgetsQuery,
} from 'graphql/types';
import { FormField } from 'common/components/FormField';
import { TextField } from 'common/components/TextField';
import { Button } from 'common/components/buttons/Button';
import { Select } from 'common/components/Select';
import { useSnackbarContext } from 'common/context';
import { Checkbox } from 'common/components/Checkbox';
import { SectionTitle } from 'common/components/SectionTitle';
import { DuplicateEmployeeMessage } from '../DuplicateEmployeeMessage';
import { validators } from 'common/constants';
import { getIntlPath } from 'common/utils';

type TInitialValues = {
    firstName: string;
    lastNamePrefix: string;
    lastName: string;
    email: string;
    personnelNumber: string;
    budgetIds: string[];
    locale: ILocaleChoices;
};

interface IProps {
    className?: string;
}

const BaseEmployeeForm = ({ className }: IProps) => {
    const history = useHistory();
    const { t } = useTranslation();
    const { setSnackbarState } = useSnackbarContext();
    const [duplicateEmployeeId, setDuplicateEmployeeId] = useState<
        string | undefined
    >();

    const { data } = useCompanyBudgetsQuery({
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const [createEmployee] = useEmployeeCreateAndInviteMutation({
        onError: (error) => {
            setSnackbarState({
                message: error.message,
                variant: 'error',
            });
        },
    });

    const formik = useFormik<TInitialValues>({
        initialValues: {
            firstName: '',
            lastNamePrefix: '',
            lastName: '',
            email: '',
            personnelNumber: '',
            budgetIds: [],
            locale: ILocaleChoices.NlBe,
        },
        validationSchema: Yup.object().shape({
            firstName: validators.firstName,
            lastName: validators.lastName,
            email: validators.email,
            budgetIds: Yup.array().min(1, 'Kies tenminste 1 pakket'),
        }),
        onSubmit: (values, { resetForm }) => {
            createEmployee({
                variables: {
                    employeeData: {
                        ...values,
                    },
                },
            }).then((res) => {
                if (!res.data || !res.data.createAndInviteEmployee) return;

                if (!res.data.createAndInviteEmployee.success) {
                    setDuplicateEmployeeId(
                        res.data.createAndInviteEmployee?.employee.id
                    );

                    return;
                }

                resetForm();

                setSnackbarState({
                    message: `${values.firstName} ${values.lastNamePrefix} ${
                        values.lastName
                    } ${t('messages.invitationSuccess')}`,
                    variant: 'success',
                });
            });
        },
    });

    const {
        errors: formikErrors,
        touched,
        values,
        handleChange,
        handleSubmit,
        setFieldValue,
    } = formik;

    const getFieldError = (fieldName: keyof TInitialValues) => {
        const fieldErrors = formikErrors[fieldName];

        if (!touched[fieldName] || !fieldErrors) {
            return '';
        }

        if (Array.isArray(fieldErrors)) {
            return fieldErrors[0];
        }

        return fieldErrors;
    };

    const { company } = data || {};

    useEffect(() => {
        // If there is only 1 available budget automatically set the budget
        if (!company) return;

        if (company.budgets.length === 1) {
            setFieldValue('budgetIds', [company.budgets[0].id]);
        }
        // Make sure the budget is set after form reset, by adding values.email as a dependecy,
    }, [company, values.email]);

    const handleBudgetClick = (id: string) => {
        if (values.budgetIds.includes(id)) {
            setFieldValue(
                'budgetIds',
                values.budgetIds.filter((budget) => budget !== id)
            );
        } else {
            setFieldValue('budgetIds', [...values.budgetIds, id]);
        }
    };

    const handleDuplicateMessageClick = (employeeId: string) => {
        history.push(`${getIntlPath('employees')}/${employeeId}`);
    };

    return (
        <Box className={className}>
            {duplicateEmployeeId && (
                <DuplicateEmployeeMessage
                    mb={5}
                    onClick={() =>
                        handleDuplicateMessageClick(duplicateEmployeeId)
                    }
                />
            )}
            <form onSubmit={handleSubmit}>
                <Box className="sectionWrapper">
                    <Box className="leftSection">
                        <SectionTitle>
                            {t('employee.personalInformation')}
                        </SectionTitle>
                        <Box className="nameRowWrapper">
                            <Box className="firstNameColumn">
                                <FormField>
                                    <TextField
                                        error={getFieldError('firstName')}
                                        id="firstName"
                                        label={t('formfields.firstName')}
                                        value={values.firstName}
                                        variant="filled"
                                        onChange={handleChange}
                                    />
                                </FormField>
                            </Box>
                            <Box className="lastNamePrefixColumn">
                                <FormField>
                                    <TextField
                                        error={getFieldError('lastNamePrefix')}
                                        id="lastNamePrefix"
                                        label={t('formfields.lastNamePrefix')}
                                        value={values.lastNamePrefix}
                                        variant="filled"
                                        onChange={handleChange}
                                    />
                                </FormField>
                            </Box>
                        </Box>
                        <FormField>
                            <TextField
                                error={getFieldError('lastName')}
                                id="lastName"
                                label={t('formfields.lastName')}
                                value={values.lastName}
                                variant="filled"
                                onChange={handleChange}
                            />
                        </FormField>
                        <FormField>
                            <TextField
                                error={getFieldError('email')}
                                id="email"
                                label={t('formfields.email')}
                                type="email"
                                value={values.email}
                                variant="filled"
                                onChange={handleChange}
                            />
                        </FormField>
                    </Box>
                    <Box className="rightSection">
                        <SectionTitle>{t('employee.extraInfo')}</SectionTitle>
                        <FormField>
                            <TextField
                                error={getFieldError('personnelNumber')}
                                id="personnelNumber"
                                label={`${t('formfields.personnelNumber')} (${t(
                                    'formfields.optional'
                                )})`}
                                value={values.personnelNumber}
                                variant="filled"
                                onChange={handleChange}
                            />
                        </FormField>
                        <FormField>
                            <Select
                                id="locale"
                                label={t('formfields.language')}
                                name="locale"
                                value={values.locale}
                                onChange={handleChange}
                            >
                                <MenuItem value={ILocaleChoices.NlBe}>
                                    {t('formfields.languageNl')}
                                </MenuItem>
                                <MenuItem value={ILocaleChoices.FrBe}>
                                    {t('formfields.languageFr')}
                                </MenuItem>
                            </Select>
                        </FormField>
                    </Box>
                </Box>
                {company && company.budgets.length > 1 && (
                    <Box className="sectionWrapper">
                        <Box className="leftSection">
                            <SectionTitle>{t('employee.budget')}</SectionTitle>

                            <FormField>
                                <FormGroup>
                                    {company.budgets.map((budget) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.budgetIds.includes(
                                                        budget.id
                                                    )}
                                                    onChange={() =>
                                                        handleBudgetClick(
                                                            budget.id
                                                        )
                                                    }
                                                />
                                            }
                                            key={budget.id}
                                            label={budget.name}
                                        />
                                    ))}
                                </FormGroup>
                            </FormField>
                        </Box>
                    </Box>
                )}
            </form>
            <Divider />
            <Box display="flex" justifyContent="flex-end" mt={6}>
                <Button
                    disabled={!values.budgetIds.length}
                    onClick={() => handleSubmit()}
                >
                    {t('employee.buttonInvite')}
                </Button>
            </Box>
        </Box>
    );
};

export const EmployeeForm = styled(BaseEmployeeForm)`
    .leftSection {
        margin-bottom: ${({ theme }) => `${theme.spacing(4)}px`};
    }

    .firstNameColumn,
    .lastNamePrefixColumn {
        width: 100%;
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['sm']}px`}) {
        .nameRowWrapper {
            display: flex;
        }

        .firstNameColumn {
            width: 60%;
            margin-right: ${({ theme }) => `${theme.spacing(4)}px`};
        }

        .lastNamePrefixColumn {
            width: 40%;
        }
    }

    @media (min-width: ${({ theme }) =>
            `${theme.breakpoints.values['lg']}px`}) {
        .sectionWrapper {
            display: flex;
            margin-bottom: ${({ theme }) => `${theme.spacing(5)}px`};
        }

        .leftSection {
            width: 50%;
            padding-right: ${({ theme }) => `${theme.spacing(18)}px`};
            margin-bottom: 0;
        }

        .rightSection {
            width: 50%;
        }
    }
`;
